import * as React from 'react';
import type { UseFormReturn } from 'react-hook-form';

import { useEditingTableCellName } from 'af-root/hooks/useEditingTableCellName';

import MaterialCostSource from 'ab-enums/materialCostSource.enum';

import MaterialRateSource from 'af-components/MaterialRateSource';
import { DisplayCell } from 'af-components/RHFTableCellComponents/Input/DisplayCell';
import type { GlyphItem } from 'af-components/RHFTableCellComponents/Input/Glyph';
import InputCellRenderer from 'af-components/RHFTableCellRenderers/InputCellRenderer';

import { createValidFromGlyph } from '../createValidFromGlyph';
import type { JobMaterialsFormInput, JobMaterialsFormOutput } from '../formModel';

type Rate = 'shippingMarkup' | 'salesMarkup' | 'tax';

type CompanyRate = 'jobShippingMarkup' | 'jobSalesMarkup' | 'jobTax';

const RATE_TO_COMPANY_RATE_VALUE: { [rate in Rate]: CompanyRate } = {
	shippingMarkup: 'jobShippingMarkup',
	salesMarkup: 'jobSalesMarkup',
	tax: 'jobTax',
};

interface RateData {
	shippingMarkup: Nullable<number>;
	salesMarkup: Nullable<number>;
	tax: Nullable<number>;
	jobShippingMarkup: number;
	jobSalesMarkup: number;
	jobTax: number;
}

interface OwnProps {
	rate: Rate;
	rateData: RateData;
	containerClassName: string;
	fieldsLength: number;
	readonly: boolean;
	displayOnly: boolean;
	rhfId: string;
	name: `materials.${number}.${'currentRate' | 'futureRate'}.${Rate}`;
	/** DB_DATE_ONLY */
	validFrom?: Nullable<string>;
	control: UseFormReturn<JobMaterialsFormInput, unknown, JobMaterialsFormOutput>['control'];
	readonlyFieldClassName?: string;
	getValues: UseFormReturn<JobMaterialsFormInput, unknown, JobMaterialsFormOutput>['getValues'];
	unregister: UseFormReturn<JobMaterialsFormInput, unknown, JobMaterialsFormOutput>['unregister'];
	onChange: (field: OwnProps['name']) => void;
}

type Props = OwnProps;

const RateInput: React.FC<Props> = (props) => {
	const {
		rate,
		rateData,
		containerClassName,
		fieldsLength,
		readonly,
		rhfId,
		name,
		displayOnly,
		validFrom,
		control,
		readonlyFieldClassName,
		getValues,
		unregister,
		onChange,
	} = props;

	const editingCellName = useEditingTableCellName();

	const defaultValue = rateData[rate] ?? rateData[RATE_TO_COMPANY_RATE_VALUE[rate]];

	const rateSource = rateData[rate] === null ? MaterialCostSource.JOB : MaterialCostSource.JOB_MATERIAL;

	const handleClick = React.useCallback(() => {
		if (readonly) {
			return;
		}
		editingCellName.setEditingTableCellName(name);
	}, [editingCellName, name, readonly]);

	const prefixGlyphs = React.useMemo<GlyphItem[]>(() => {
		const glyphs: GlyphItem[] = [{
			type: 'node',
			value: <MaterialRateSource rateSource={rateSource} />,
		}];

		if (validFrom) {
			glyphs.push(createValidFromGlyph(validFrom));
		}

		return glyphs;
	}, [rateSource, validFrom]);

	return (
		<div className={containerClassName} onClick={handleClick}>
			{displayOnly ? (
				<DisplayCell
					elementKey={rhfId}
					name={name}
					prefixGlyphs={prefixGlyphs}
					readonly={true}
					type="percent"
					value={defaultValue}
				/>
			) : (
				<InputCellRenderer
					control={control}
					defaultValue={defaultValue.toString()}
					fieldsLength={fieldsLength}
					getValues={getValues}
					name={name}
					onChange={onChange}
					prefixGlyphs={prefixGlyphs}
					readonly={readonly}
					readonlyFieldClassName={readonlyFieldClassName}
					rhfId={rhfId}
					type="percent"
					unregister={unregister}
				/>
			)}
		</div>
	);
};

export default React.memo(RateInput);
